import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from '../../animations/useReducedMotion';
import { ArrowRight } from '../../design/icons';
import { comparePayloadEfficiency, parseOpenUI } from '../../lib/openui';
import OpenUIRenderer from '../Demos/OpenUIRenderer';
import './hero-streaming-showcase.css';

export interface ShowcaseTab {
    id: string;
    label: string;
    prompt: string;
    staticText: string;
    openuiProgram: string;
    ctaLabel: string;
    ctaTo: string;
}

const SHOWCASE_TABS: readonly ShowcaseTab[] = [
    {
        id: 'agents',
        label: 'Agent Systems',
        prompt: 'How are Abhijith’s production AI agents architected and guarded?',
        staticText: [
            'In production enterprise environments (Zalando SE, Salesforce Agentforce, LangGraph, and Google ADK), autonomous agents cannot be given unchecked write access to core business records.',
            '',
            'Instead, the agent loop uses a RETURN_CONTROL handoff:',
            '- The model selects the tool and extracts typed parameters.',
            '- Control returns to the application boundary before any external side effect executes.',
            '- Deterministic guardrails validate schema, permissions, and rate limits.',
            '- Only verified calls reach Apex REST or backend microservices, with full OpenTelemetry trace spans.',
        ].join('\n'),
        openuiProgram: [
            'root = Card([header, kpis, chart, table])',
            'header = Header("Production Agent Architecture", "Application-owned RETURN_CONTROL boundary", "GUARDED")',
            'kpis = Stack([mLat, mPass, mTok])',
            'mLat = Metric("p95 Handoff", "340ms", "-18%")',
            'mPass = Metric("Schema Guard", "99.4%", "verified")',
            'mTok = Metric("Payload Size", "142 tok", "-57% vs JSON")',
            'chart = BarChart(["Intent Parse", "Guardrail Check", "Apex REST", "Trace Commit"], [38, 14, 196, 92], "ms")',
            'table = Table(["Layer", "Runtime", "Boundary"], [r1, r2, r3])',
            'r1 = Row("Orchestration", "LangGraph · ADK", Tag("stateful", "good"))',
            'r2 = Row("Verification", "JSON Schema · RBAC", Tag("enforced", "good"))',
            'r3 = Row("Observability", "OpenTelemetry · Honeycomb", Tag("traced", "neutral"))',
        ].join('\n'),
        ctaLabel: 'Read Agentic AI in Production',
        ctaTo: '/article/agentic-ai-salesforce',
    },
    {
        id: 'rag',
        label: 'RAG & Retrieval',
        prompt: 'Show the retrieval and reranking pipeline performance across the corpus.',
        staticText: [
            'Nearest-neighbour vector search alone misses exact keyword identifiers (like SKU codes, error IDs, or class names), while lexical search alone misses semantic paraphrases.',
            '',
            'The hybrid retrieval pipeline combines:',
            '1. On-device BM25 lexical scoring with length normalisation.',
            '2. Dense 384-d MiniLM embeddings computed in WebAssembly.',
            '3. Reciprocal Rank Fusion (RRF) to merge both rankers without calibration drift.',
            '4. Cross-encoder reranking over the top-K candidates before context assembly.',
        ].join('\n'),
        openuiProgram: [
            'root = Card([header, kpis, chart])',
            'header = Header("Hybrid RAG + Reranking", "BM25 + MiniLM Reciprocal Rank Fusion", "LIVE")',
            'kpis = Stack([mRec, mRerank, mGate])',
            'mRec = Metric("Top-5 Recall", "94.2%", "+15.1%")',
            'mRerank = Metric("Rerank Latency", "68ms", "cross-encoder")',
            'mGate = Metric("Local Gate Hit", "64.5%", "0 cloud tokens")',
            'chart = BarChart(["BM25 Only", "Dense MiniLM", "RRF Fused", "Cross-Encoder"], [71, 79, 89, 94], "%")',
        ].join('\n'),
        ctaLabel: 'Run the Live RAG Pipeline Lab',
        ctaTo: '/labs/rag-pipeline',
    },
    {
        id: 'training',
        label: 'Transformer & WASM',
        prompt: 'Can we train a transformer and inspect LoRA adapters directly in the browser?',
        staticText: [
            'Most ML explanations stop at high-level diagrams. Backprop Forge compiles a hand-derived backward pass for a 106k-parameter transformer into SIMD WebAssembly so every gradient flows live in your browser tab.',
            '',
            'Alongside full backprop, the LoRA Rank Explorer decomposes weight updates into low-rank factors B and A (rank r=1..16), showing reconstruction error and VRAM savings in real time.',
        ].join('\n'),
        openuiProgram: [
            'root = Card([header, kpis, chart])',
            'header = Header("In-Browser Transformer & LoRA", "Hand-derived backward pass in SIMD WASM", "106K PARAMS")',
            'kpis = Stack([mSpeed, mRank, mVram])',
            'mSpeed = Metric("WASM Kernel", "4.2x", "vs JS float32")',
            'mRank = Metric("LoRA Rank r=8", "0.42%", "trainable params")',
            'mVram = Metric("Memory Saved", "-62%", "peak footprint")',
            'chart = BarChart(["JS Baseline", "WebAssembly", "WASM + SIMD", "WebGPU"], [24, 68, 96, 100], "%")',
        ].join('\n'),
        ctaLabel: 'Open Backprop Forge Lab',
        ctaTo: '/labs/backprop-forge',
    },
    {
        id: 'career',
        label: 'Career & Fit Check',
        prompt: 'Summarise Abhijith’s 11-year engineering trajectory across India, USA, and Germany.',
        staticText: [
            '11+ years building enterprise platforms and production AI systems across four companies and three countries:',
            '- Zalando SE (Berlin, 2020–Present): Senior Systems Architect & AI Specialist.',
            '- LemonOne (Berlin): Lead Salesforce Engineer.',
            '- adidas (Herzogenaurach): Senior Platform Engineer.',
            '- UMKC (Kansas City, MS CS) & Infosys (Mysuru, Systems Engineer).',
        ].join('\n'),
        openuiProgram: [
            'root = Card([header, kpis, table])',
            'header = Header("11 Years in Production", "India → USA → Germany · Enterprise & AI Systems", "BERLIN")',
            'kpis = Stack([mYrs, mCerts, mWork])',
            'mYrs = Metric("Experience", "11+ yrs", "4 companies")',
            'mCerts = Metric("Credentials", "10 Certs", "Architect")',
            'mWork = Metric("Evidence Index", "18 Notes · 30 Labs", "verified")',
            'table = Table(["Organisation", "Role", "Location"], [c1, c2, c3, c4])',
            'c1 = Row("Zalando SE", "Senior Systems Architect", Tag("2020–Now", "good"))',
            'c2 = Row("LemonOne", "Lead Salesforce Engineer", Tag("Berlin", "neutral"))',
            'c3 = Row("adidas", "Senior Platform Engineer", Tag("Germany", "neutral"))',
            'c4 = Row("Infosys", "Systems Engineer", Tag("India", "neutral"))',
        ].join('\n'),
        ctaLabel: 'Match a Job Description in Fit Check',
        ctaTo: '/fit',
    },
];

/**
 * OpenUI-inspired notched dual-panel streaming comparison stage for the
 * portfolio homepage. Streams plain-text markdown on the left against
 * hoisted OpenUI Lang generative components on the right, with a floating
 * pill tab switcher along the bottom edge.
 */
export const HeroStreamingShowcase: React.FC = () => {
    const reducedMotion = useReducedMotion();
    const [activeIdx, setActiveIdx] = useState(0);
    const [progress, setProgress] = useState(100);
    const [streaming, setStreaming] = useState(false);

    const activeTab = SHOWCASE_TABS[activeIdx];

    useEffect(() => {
        if (reducedMotion) {
            setProgress(100);
            setStreaming(false);
            return;
        }
        setProgress(16);
        setStreaming(true);
        const timer = window.setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    window.clearInterval(timer);
                    setStreaming(false);
                    return 100;
                }
                return Math.min(100, prev + 6);
            });
        }, 45);
        return () => window.clearInterval(timer);
    }, [activeIdx, reducedMotion]);

    const staticSlice = useMemo(() => {
        const chars = Math.max(1, Math.round((progress / 100) * activeTab.staticText.length));
        return activeTab.staticText.slice(0, chars);
    }, [activeTab.staticText, progress]);

    const openuiSlice = useMemo(() => {
        const chars = Math.max(1, Math.round((progress / 100) * activeTab.openuiProgram.length));
        return activeTab.openuiProgram.slice(0, chars);
    }, [activeTab.openuiProgram, progress]);

    const parsed = useMemo(() => parseOpenUI(openuiSlice), [openuiSlice]);
    const stats = useMemo(() => comparePayloadEfficiency(activeTab.openuiProgram), [activeTab.openuiProgram]);

    return (
        <section className="oui-showcase" aria-label="Interactive Generative UI comparison">
            <div className="oui-showcase__header">
                <div>
                    <span className="oui-showcase__eyebrow">
                        Live Architecture Stream <span className="oui-showcase__eyebrow-chip">OpenUI Lang 1.0</span>
                    </span>
                    <h2 className="oui-showcase__title">
                        Don&rsquo;t just read claims. <em>Watch the system render.</em>
                    </h2>
                </div>
                <div className="oui-showcase__meta">
                    <span className="oui-showcase__stat">
                        <strong>-{stats.reductionPct}%</strong> fewer tokens than JSON
                    </span>
                    <button
                        type="button"
                        className="oui-showcase__replay tap-hit"
                        onClick={() => {
                            setProgress(14);
                            setStreaming(true);
                        }}
                    >
                        {streaming ? 'Streaming…' : '↻ Replay stream'}
                    </button>
                </div>
            </div>

            <div className="oui-showcase__stage">
                <div className="oui-showcase__grid">
                    {/* Left Panel: Traditional Static Text / Wall of Prose */}
                    <div className="oui-showcase__panel oui-showcase__panel--without">
                        <div className="oui-showcase__notch">Without OpenUI · Plain Prose</div>
                        <div className="oui-showcase__prompt">{activeTab.prompt}</div>
                        <div className="oui-showcase__stream-body">
                            <pre className="oui-showcase__prose">{staticSlice}</pre>
                            <div className="oui-showcase__code-preview">
                                <span>Streamed OpenUI Lang ({stats.openuiTokens} tok vs {stats.jsonTokens} JSON tok)</span>
                                <pre>{openuiSlice}</pre>
                            </div>
                        </div>
                    </div>

                    {/* Right Panel: With OpenUI Generative UI */}
                    <div className="oui-showcase__panel oui-showcase__panel--with">
                        <div className="oui-showcase__notch oui-showcase__notch--accent">
                            With OpenUI · Streamed Live UI
                        </div>
                        <div className="oui-showcase__prompt">{activeTab.prompt}</div>
                        <div className="oui-showcase__stream-body">
                            {parsed.root ? (
                                <OpenUIRenderer node={parsed.root} />
                            ) : (
                                <div className="oui-showcase__placeholder"> Materialising root card…</div>
                            )}
                            <div className="oui-showcase__panel-footer">
                                <Link to={activeTab.ctaTo} className="oui-showcase__cta tap-hit">
                                    <span>{activeTab.ctaLabel}</span>
                                    <span className="oui-showcase__cta-badge" aria-hidden="true">
                                        <ArrowRight size={14} />
                                    </span>
                                </Link>
                                <Link to="/labs/openui-streaming" className="oui-showcase__lab-link tap-hit">
                                    Open full compiler lab ↗
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Floating Pill Tab Bar at the bottom edge of the stage */}
                <div className="oui-showcase__tabs-bar">
                    <div className="oui-showcase__tabs" role="tablist" aria-label="Portfolio domains">
                        {SHOWCASE_TABS.map((tab, idx) => {
                            const isSelected = idx === activeIdx;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={isSelected}
                                    className={`oui-showcase__tab tap-hit${isSelected ? ' is-active' : ''}`}
                                    onClick={() => setActiveIdx(idx)}
                                >
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HeroStreamingShowcase;
